import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { triggerTabNotification, playChimeSound } from '../../../utils';

/**
 * Hook para gestionar la conexión en tiempo real vía SignalR con la sala del ticket,
 * reconciliar mensajes optimistas y sincronizar eventos de actividad.
 */
export const useTicketSignalR = ({
  activeTicketId,
  token,
  setMessages,
  markCurrentAsRead,
  fetchTicketDetail,
  onTicketUpdated,
  onRefreshHistory,
  fetchParticipants,
  ticketDetail,
  propTicket,
  idUsuario,
  nombreCompleto
}) => {
  const [typingUsers, setTypingUsers] = useState([]);
  const typingTimeoutsRef = useRef({});

  const hubConnectionRef = useRef(null);
  const fetchParticipantsRef = useRef(fetchParticipants);
  fetchParticipantsRef.current = fetchParticipants;
  const fetchTicketDetailRef = useRef(fetchTicketDetail);
  fetchTicketDetailRef.current = fetchTicketDetail;

  const onTicketUpdatedRef = useRef(onTicketUpdated);
  onTicketUpdatedRef.current = onTicketUpdated;

  const onRefreshHistoryRef = useRef(onRefreshHistory);
  onRefreshHistoryRef.current = onRefreshHistory;

  const ticketDetailRef = useRef(ticketDetail);
  ticketDetailRef.current = ticketDetail;

  const markCurrentAsReadRef = useRef(markCurrentAsRead);
  markCurrentAsReadRef.current = markCurrentAsRead;

  useEffect(() => {
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    let isCancelled = false;
    let connection = null;

    const startSignalR = async () => {
      try {
        const resp = await fetch('/api/SupportEngine/GetBaseUrl', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        if (!resp.ok) {
          console.error(`[TicketDetailView] Error al consultar /api/SupportEngine/GetBaseUrl: HTTP ${resp.status}`);
          return;
        }
        const { baseUrl } = await resp.json();
        if (isCancelled) return;

        const hubUrl = `${baseUrl}/hubs/support-chat`;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        hubConnectionRef.current = connection;

        connection.onreconnected(() => {
          connection.invoke('JoinTicketGroup', parseInt(activeTicketId)).catch(e => console.error('Error re-uniendo al grupo:', e));
        });

        await connection.start();
        if (isCancelled) {
          connection.stop().catch(() => { });
          return;
        }

        // Unirse a la sala del ticket
        await connection.invoke('JoinTicketGroup', parseInt(activeTicketId));

        // Escuchar mensajes entrantes en tiempo real
        connection.on('ReceiveMessage', async newMsg => {
          if (!newMsg) return;

          // Si el evento recibido es una acción administrativa (ej. asignación) y no un mensaje de chat, ignorar
          if (newMsg.action) return;

          const msgId = newMsg.idMensaje ?? newMsg.messageId;
          const msgText = newMsg.vTextoMensaje ?? newMsg.messageText ?? '';
          const mediaId = newMsg.vMediaId ?? newMsg.mediaId;
          const isAgent = newMsg.bEsAgente ?? newMsg.isAgent;
          const tipoMensajeId = newMsg.idCatTipoMensaje ?? newMsg.messageTypeId ?? 1;
          const isNote = tipoMensajeId === 20 || Boolean(newMsg.bEsNota ?? newMsg.isNote);

          const formattedMsg = {
            idMensaje: msgId,
            idTicket: newMsg.idTicket ?? newMsg.ticketId,
            bEsAgente: isAgent ? 1 : 0,
            bEsNota: isNote ? 1 : 0,
            idCatTipoMensaje: tipoMensajeId,
            vTextoMensaje: msgText,
            vMediaId: mediaId,
            vMediaMimeType: newMsg.vMediaMimeType ?? newMsg.mediaMimeType,
            vMediaUrl: newMsg.vMediaUrl ?? newMsg.mediaUrl,
            vFromNumber: newMsg.vFromNumber ?? newMsg.fromNumber,
            vFromUserId: newMsg.vFromUserId ?? newMsg.fromUserId,
            vCreatedUser: newMsg.vCreatedUser ?? newMsg.createdUser,
            dCreatedDate: newMsg.dCreatedDate ?? newMsg.createdDate ?? new Date().toISOString(),
            vWaMessageId: newMsg.vWaMessageId ?? newMsg.waMessageId,
            vContextWaMessageId: newMsg.vContextWaMessageId ?? newMsg.contextWaMessageId,
            idMensajeCitado: newMsg.idMensajeCitado,
            vQuotedText: newMsg.vQuotedText,
            vQuotedAuthor: newMsg.vQuotedAuthor,
            bQuotedEsAgente: newMsg.bQuotedEsAgente,
            idQuotedTipoMensaje: newMsg.idQuotedTipoMensaje
          };

          setMessages(prev => {
            // 1. Evitar duplicados solo si tiene un ID válido (evita que undefined === undefined descarte mensajes)
            const exists = prev.some(m =>
              (msgId && (m.idMensaje === msgId || m.messageId === msgId)) ||
              (mediaId && (m.vMediaId === mediaId || m.mediaId === mediaId))
            );
            if (exists) {
              return prev;
            }

            // 2. Si es un mensaje de agente y hay un mensaje temporal optimista pendiente con el mismo contenido o tipo, reemplazarlo
            const tempIndex = prev.findIndex(m =>
              (m.isTemp || m.idMensaje > 1000000000000) &&
              (
                (m.vTextoMensaje && msgText && m.vTextoMensaje.trim() === msgText.trim()) ||
                (m.vMediaUrl && formattedMsg.vMediaUrl && m.vMediaUrl === formattedMsg.vMediaUrl) ||
                (m.vMediaId && mediaId && m.vMediaId === mediaId) ||
                (Boolean(m.isTemp) && (m.idCatTipoMensaje === 2 || m.idCatTipoMensaje === 5 || m.idCatTipoMensaje === 6))
              )
            );

            if (tempIndex !== -1) {
              const updated = [...prev];
              updated[tempIndex] = {
                ...formattedMsg,
                vMediaUrl: formattedMsg.vMediaUrl || updated[tempIndex].vMediaUrl,
                isTemp: false
              };
              return updated;
            }
            return [...prev, formattedMsg];
          });

          const isFromClient = isAgent === 0 || isAgent === false || formattedMsg.bEsAgente === 0;

          // Si la pestaña está en segundo plano o el usuario está en otra ventana/pestaña, animar pestaña y alertar
          const isTabInactive = typeof document === 'undefined' || document.hidden || !document.hasFocus();

          // Solo mantenerlo como leído automáticamente si el asesor TIENE la pestaña activa y en foco
          if (!isTabInactive && markCurrentAsReadRef.current) {
            markCurrentAsReadRef.current(activeTicketId);
          }

          if (isTabInactive && isFromClient) {
            const customerName = ticketDetailRef.current?.vCustomerName || propTicket?.vCustomerName || 'Cliente';
            triggerTabNotification({
              alertText: '¡Nuevo mensaje!',
              detailText: `Ticket #${activeTicketId} · ${customerName}`
            });

            playChimeSound();

            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
              try {
                const notif = new Notification(`Ticket #${activeTicketId} · ${customerName}`, {
                  body: formattedMsg.vTextoMensaje || 'Nuevo mensaje recibido',
                  icon: '/favicon.ico',
                  tag: `ticket-${activeTicketId}`,
                  renotify: true
                });
                notif.onclick = () => {
                  window.focus();
                  notif.close();
                };
              } catch {
                // Fallback silencioso
              }
            }
          }

          // Notificar reactivamente a la lista de tickets para actualizar última actividad
          window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
            detail: {
              idTicket: parseInt(activeTicketId),
              bEsAgente: isFromClient ? 0 : 1,
              isAgent: !isFromClient,
              vTextoMensaje: newMsg.vTextoMensaje || newMsg.textoMensaje || newMsg.messageText || '',
              dUltimaActividad: newMsg.dCreatedDate || newMsg.createdDate || new Date().toISOString(),
              isCurrentlyOpen: true
            }
          }));

          // Si el mensaje proviene de un asesor, refrescar colaboradores por si se unió un nuevo agente
          if (!isFromClient && fetchParticipantsRef.current) {
            fetchParticipantsRef.current();
          }

          // Si el ticket estaba cerrado y un cliente responde (reapertura), refrescamos estado e historial
          const currentStatus = Number(ticketDetailRef.current?.iEstadoTicket ?? propTicket?.iEstadoTicket);
          if (isFromClient && (currentStatus === 2 || !ticketDetailRef.current)) {
            if (fetchTicketDetailRef.current) {
              await fetchTicketDetailRef.current();
            }
            if (onTicketUpdatedRef.current) {
              onTicketUpdatedRef.current();
            }
            if (onRefreshHistoryRef.current) {
              onRefreshHistoryRef.current();
            }
          }

          // Si el mensaje proviene de AKASIABOT (aviso o cierre automático del sistema)
          const isBot = newMsg.vCreatedUser === 'AKASIABOT' || newMsg.createdUser === 'AKASIABOT';
          if (isBot) {
            if (fetchTicketDetailRef.current) {
              const updated = await fetchTicketDetailRef.current(true);
              if (updated && Number(updated.iEstadoTicket) === 2) {
                window.location.reload();
                return;
              }
            }
            if (onTicketUpdatedRef.current) {
              onTicketUpdatedRef.current();
            }
          }
        });

        // Escuchar solicitud de refresco de detalles del ticket (ej. mensaje desde otro número del cliente)
        connection.on('ReceiveTicketDetailRefresh', async refreshData => {
          if (fetchTicketDetailRef.current) {
            const updated = await fetchTicketDetailRef.current(true);
            if (updated && Number(updated.iEstadoTicket) === 2) {
              const motivo = (updated?.vMotivoCerrado || '').toLowerCase();
              if (motivo.includes('inactividad') || motivo.includes('bot') || updated?.bCerradoPorInactividadBot) {
                window.location.reload();
                return;
              }
            }
          }
          if (fetchParticipantsRef.current) {
            await fetchParticipantsRef.current();
          }
          if (onTicketUpdatedRef.current) {
            onTicketUpdatedRef.current();
          }
          if (onRefreshHistoryRef.current) {
            onRefreshHistoryRef.current();
          }
          if (refreshData?.idClienteAkasia) {
            window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
              detail: {
                idTicket: parseInt(activeTicketId),
                idClienteAkasia: refreshData.idClienteAkasia
              }
            }));
          }
        });

        // Escuchar cuando otro colaborador escribe en el ticket
        connection.on('UserTyping', data => {
          if (!data) return;
          console.log('🔔 [SignalR] Evento UserTyping recibido:', data);

          const eventTicketId = Number(data.idTicket ?? data.IdTicket);
          if (eventTicketId !== Number(activeTicketId)) return;

          const targetUserId = Number(data.idUsuario ?? data.IdUsuario);
          const targetUserName = data.nombreUsuario ?? data.NombreUsuario ?? 'Colaborador';
          const isTyping = Boolean(data.isTyping ?? data.IsTyping);

          if (isTyping) {
            setTypingUsers(prev => {
              const exists = prev.some(u => Number(u.idUsuario) === targetUserId);
              if (exists) return prev;
              return [...prev, { idUsuario: targetUserId, nombreUsuario: targetUserName }];
            });

            if (typingTimeoutsRef.current[targetUserId]) {
              clearTimeout(typingTimeoutsRef.current[targetUserId]);
            }
            typingTimeoutsRef.current[targetUserId] = setTimeout(() => {
              setTypingUsers(prev => prev.filter(u => Number(u.idUsuario) !== targetUserId));
              delete typingTimeoutsRef.current[targetUserId];
            }, 5000);
          } else {
            if (typingTimeoutsRef.current[targetUserId]) {
              clearTimeout(typingTimeoutsRef.current[targetUserId]);
              delete typingTimeoutsRef.current[targetUserId];
            }
            setTypingUsers(prev => prev.filter(u => Number(u.idUsuario) !== targetUserId));
          }
        });

        // Escuchar actualización de mensajes en tiempo real (ej. edición de nota interna)
        connection.on('UpdateMessage', updatedMsg => {
          if (!updatedMsg) return;
          const msgId = updatedMsg.idMensaje ?? updatedMsg.messageId;
          const msgText = updatedMsg.vTextoMensaje ?? updatedMsg.messageText;
          if (!msgId) return;

          setMessages(prev => prev.map(m => {
            const currentId = m.idMensaje ?? m.id;
            if (currentId === msgId) {
              return {
                ...m,
                vTextoMensaje: msgText ?? m.vTextoMensaje
              };
            }
            return m;
          }));
        });
      } catch (err) {
        console.warn('[TicketDetailView] Error en conexión SignalR:', err);
      }
    };

    startSignalR();

    return () => {
      isCancelled = true;
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
      setTypingUsers([]);
      if (connection) {
        connection.invoke('LeaveTicketGroup', parseInt(activeTicketId)).catch(() => { });
        connection.stop().catch(() => { });
      }
    };
  }, [activeTicketId, token, setMessages]);

  const sendTyping = useCallback((isTyping) => {
    const conn = hubConnectionRef.current;
    if (conn && conn.state === signalR.HubConnectionState.Connected) {
      const ticketIdNum = parseInt(activeTicketId, 10);
      const userIdNum = parseInt(idUsuario, 10) || 0;
      const userNameStr = String(nombreCompleto || 'Colaborador');

      console.log('✍️ [SignalR] Invocando SendTyping en Hub:', { ticketIdNum, userIdNum, userNameStr, isTyping });
      conn.invoke('SendTyping', ticketIdNum, userIdNum, userNameStr, Boolean(isTyping))
        .catch(err => console.error('❌ [SignalR] Error invocando SendTyping en Hub:', err));
    } else {
      console.warn('⚠️ [SignalR] No se envió SendTyping. Estado actual de la conexión:', conn?.state);
    }
  }, [activeTicketId, idUsuario, nombreCompleto]);

  return {
    sendTyping,
    typingUsers
  };
};
